<script>
  import { createNamespacedHelpers } from 'vuex';
  import Preview from '../hitch/Preview';
  import EditLayout from '../hitch/EditLayout';
  import AddToCart from '../hitch/AddToCart';
  import InputText from '../hitch/InputText';
  import RichTextLayout from '../RichTextLayout';

  import WidgetCallout from "../widgets/WidgetCallout";
  import WidgetButtonLink from "../widgets/WidgetButtonLink";
  import WidgetCustomCode from "../widgets/WidgetCustomCode";
  import WidgetImageGallery from "../widgets/WidgetImageGallery";
  import WidgetImageGalleryWithText from "../widgets/WidgetImageGalleryWithText";
  import WidgetSingleImageLinkOption from "../widgets/WidgetSingleImageLinkOption";

  const { mapGetters } = createNamespacedHelpers('hitch');

  export default {
    name: 'ViewHitch',
    components: {
      Preview,
      EditLayout,
      AddToCart,
      InputText,
      RichTextLayout,
      WidgetCallout,
      WidgetButtonLink,
      WidgetCustomCode,
      WidgetImageGallery,
      WidgetImageGalleryWithText,
      WidgetSingleImageLinkOption
    },
    props: {
      /**
       * the props defined here will be modified to represent the original data gotten
       * from craft later
       */
      title: {
        type: String,
        required: true,
      },
      content: {
        type: String,
        required: true,
      },
      bulkPricing: {
        type: Array,
        default: () => [],
      },
      freeShipping: {
        type: Array,
        default: () => [],
      },
      csrf: {
        type: String,
        default: ''
      },
      sku: {
        type: String,
        default: ''
      },
      entryId: {
        type: Number,
        default: 0
      },
      /** The ID of the large letter style to use */
      largeLetterId: {
        type: [String, Number],
        required: true
      },
      /** The ID of the large letter style to use */
      smallLetterId: {
        type: [String, Number],
        required: true
      },
      baseProductIds: {
        /** id belonging to license plate */
        type: Array,
        required: true
      },
      allProducts: {
        /** Array of all the customizable products (for navigation between them) */
        type: Array,
        default: () => []
      },
      allCustomizationStyles: {
        /** Array of all the customization styles ("Double" or "Monogram" for now) */
        type: Array,
        default: () => []
      },
      /** Sidebar widgets that should appear on the page */
      widgets: {
        type: Array,
        default: () => []
      },
      /** Promotion Categories on customizable product */
      promotionCategories: {
        type: String,
        default: '',
      }
    },
    computed: {
      ...mapGetters([
        'getSelectedBaseProduct',
        'getSelectedVariant',
        'getUserText',
        'getCustomizationStyle',
        'getLargeLetterStyle',
        'getSmallLetterStyle',
        'getLoadingBase',
        'getLoadingLargeLetters',
        'getLoadingSmallLetters'
      ]),
      /** Determines which letter style to feed to the letter description area in the rich text */
      currentLetterStyle() {
        let letterStyle = {};
        if (
          this.getCustomizationStyle.value === 'monogram' &&
          (this.getUserText.length === 0 || this.getUserText.length === 2)
        ) {
          letterStyle = this.getSmallLetterStyle;
        } else {
          letterStyle = this.getLargeLetterStyle;
        }
        return letterStyle;
      },
      /** Returns just the special characters for the legend area of the rich text */
      getSpecialChars() {
        let chars = [];
        if (this.currentLetterStyle.chars) {
          chars = this.currentLetterStyle.chars.filter(
            (char) => char.type === 'special'
          );
        }
        return chars;
      },
      /** Determines if a letter style has any lowercase letters */
      hasLowercase() {
        let lowerFound = false;
        if (this.currentLetterStyle.chars) {
          lowerFound = this.currentLetterStyle.chars.filter((l) => l.type === 'lower').length > 0;
        }
        return lowerFound;
      },
      /** Parses the text to display for bulk pricing */
      bulkPricingText() {
        let text = '';
        if (this.bulkPricing.length > 0) {
          this.bulkPricing.forEach((row) => {
            text += row.discount + '% off ' + row.qty + ' or more<br>';
          });
        }
        return text;
      },
      /** Parses the text to display for free shipping */
      shippingText() {
        let text = '';
        if (this.freeShipping.length > 0) {
          this.freeShipping.forEach((string) => {
            text +=
              '<h4>' +
              string.heading +
              '</h4><p>' +
              string.note +
              '</p>';
          });
        }
        return text;
      },
    },
    created() {
      // Initialize the product in the store
      this.$store.dispatch('hitch/initProduct', {
        baseIds: this.baseProductIds.join(),
        baseType: 'customHitchCover',
        customizationStyles: this.allCustomizationStyles,
        largeLetterId: parseInt(this.largeLetterId),
        smallLetterId: parseInt(this.smallLetterId)
      });

      // set the entry data into the store
      this.$store.commit('hitch/setEntryId', this.entryId);
      this.$store.commit('hitch/setAllProducts', this.allProducts);

      // set csrf token in store
      this.$store.commit('hitch/setCsrfToken', this.csrf);
    },
    methods: {
      resolveWidget(type) {
        return `Widget${type.charAt(0).toUpperCase()}${type.slice(1)}`;
      }
    }
  };
</script>

<template>
  <div class="product-view--plate js-custom-plate">

    <div class="product-view__main">

      <header class="product-view__header m-mbl">
        <h1 class="product-view__title">{{ title }}</h1>
      </header>

      <Preview
        v-if="!getLoadingBase && !getLoadingLargeLetters && !getLoadingSmallLetters"
        :variant="getSelectedVariant"
      />

      <div v-else>
        <div class="product-view__display m-plate">
          <div class="letter-display--plate js-custom-plate__display-wrap x-loading">
            <span class="loading" />
          </div>
        </div>
      </div>

      <EditLayout />

      <RichTextLayout>
        <template v-slot:title>{{ title }}</template>

        <template v-slot:content>
          <div v-html="content" />
        </template>

        <template v-if="!getLoadingLargeLetters && !getLoadingSmallLetters" v-slot:letterCase>
          <div v-if="hasLowercase">
            <strong>Lowercase:</strong>
            Height
            <span>{{ currentLetterStyle.lowercase.height }}</span>" Average Width
            <span>{{ currentLetterStyle.lowercase.width }}</span>"<br />
          </div>
          <strong>Uppercase:</strong>
          Height
          <span>{{ currentLetterStyle.uppercase.height }}</span>" Average Width
          <span>{{ currentLetterStyle.uppercase.width }}</span>"<br />
          <strong>Style: </strong>
          <span>{{ currentLetterStyle.finish.label }}, {{ currentLetterStyle.style }}</span>
        </template>

        <template v-if="!getLoadingLargeLetters && !getLoadingSmallLetters" v-slot:legend>
          <li v-for="symbol in getSpecialChars" :key="symbol.id">
            For {{ symbol.class }}, type {{ symbol.key }}
          </li>
        </template>
      </RichTextLayout>

    </div>

    <AddToCart
      v-if="getSelectedVariant"
      :bulkPricing="bulkPricing"
      :sku="sku"
      :title="title"
      :entry-id="entryId"
      bundle-id="4"
      :redirectUrl="'/store/cart'"
      :promotion-categories="promotionCategories"
    >

      <template v-slot:inputs>
        <InputText field-id="secondaryInput" />
      </template>

      <template v-if="bulkPricing.length > 0" v-slot:bulkPricing>
        <div class="product-cart__bulk-details">
          <p>
            Buy in Bulk and Save<br />
            <span v-html="bulkPricingText" />
          </p>
        </div>
      </template>

      <template v-if="freeShipping" v-slot:shipping>
        <div class="shipping" v-html="shippingText" />
      </template>

      <template v-if="widgets" v-slot:widgets>
        <component
          v-for="widget in widgets"
          :key="widget.id"
          :is="resolveWidget(widget.type)"
          v-bind="widget"
        />
      </template>
    </AddToCart>

    <div v-else class="product-view__cart">
      <div class="product-cart--letters x-loading">
        <span class="loading" />
      </div>
    </div>

  </div>
</template>
